// Billing in the plan tab (plan step 5.2; D52, D88, D107). The owner picks Pro's interval,
// adds a payment method in Stripe's Payment Element (an iframe: the card never reaches Apsio),
// and subscribes; the card's issuing country decides the currency, so a Brazilian card, Pix
// or boleto asks for the CPF or CNPJ and the address the NFS-e needs. Afterwards the panel
// shows the subscription, its invoices and Stripe's billing portal. Hidden while billing is
// off (the API answers 503 not_configured): then the section only says how paying works. A card issued in Brazil cannot pay a USD subscription (Stripe
// charges it in reais only): the panel then offers the switch to reais (D88 addendum).
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
// The pure entry: Stripe.js loads only when an owner adds a payment method, never on import.
import type { Stripe } from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'
import { useQuery } from '@tanstack/react-query'
import { type FormEvent, useId, useState } from 'react'
import { isApiError } from '../api/client'
import {
  billingPaymentMethod,
  billingPortal,
  billingQuery,
  billingSetupIntent,
  billingSwitchSetupIntent,
  useSubscribe,
  useSwitchCurrency,
} from '../api/queries'
import type { Billing, BrazilAddress, Role } from '../api/types'
import { Section } from '../components/d'
import { Locked } from '../components/Locked'
import { ErrorState, Loading } from '../components/states'
import { PLAN_KEY } from '../components/TrialMeter'
import { type MessageId, useI18n } from '../i18n'
import { day } from '../lib/format'

/** The first payment did not go through (a declined 3-D Secure, a closed voucher). */
class PaymentError extends Error {}

/**
 * What a failed subscribe tells the owner: a first payment not finished, a declined card (on
 * the Brazil step too), details Stripe refused, or a subscription already there.
 */
export function failureMessage(error: unknown, brazilStep: boolean): MessageId {
  if (error instanceof PaymentError) return 'billing.paymentFailed'
  if (isApiError(error, 400))
    return brazilStep && error.code !== 'payment_declined'
      ? 'billing.invalidDetails'
      : 'billing.cardFailed'
  if (isApiError(error, 409)) return 'billing.conflict'
  // The server cannot bill this plan right now (its prices are not synced, or billing is off): not
  // the owner's card, and nothing was charged (APS-538). Only the API's own codes: a plain 503
  // from a proxy or an outage stays the generic message (api/client.ts).
  if (isApiError(error, 503) && (error.code === 'unavailable' || error.code === 'not_configured'))
    return 'billing.planUnavailable'
  return 'state.failed'
}

const stripes = new Map<string, Promise<Stripe | null>>()
/** Stripe.js, loaded once per publishable key, only when the owner adds a payment method. */
const stripeFor = (key: string) => {
  let stripe = stripes.get(key)
  if (!stripe) {
    stripe = loadStripe(key)
    stripes.set(key, stripe)
  }
  return stripe
}

/** Subscription states that are over: the section offers to subscribe again. */
export const ENDED = ['canceled', 'incomplete_expired']

/** Stripe's billing portal, where the owner changes the card, the plan's interval or cancels. */
export async function openBillingPortal(orgId: string): Promise<void> {
  window.location.assign((await billingPortal(orgId)).url)
}

/** The subscription's state as a status word (Console D): its glyph, colour and word. */
const STATUS: Record<string, { key: MessageId; s: 'ok' | 'fire' | 'mute'; glyph: string }> = {
  active: { key: 'billing.status.active', s: 'ok', glyph: '✓' },
  trialing: { key: 'billing.status.active', s: 'ok', glyph: '✓' },
  past_due: { key: 'billing.status.pastDue', s: 'fire', glyph: '!' },
  unpaid: { key: 'billing.status.unpaid', s: 'fire', glyph: '!' },
  incomplete: { key: 'billing.status.incomplete', s: 'mute', glyph: '○' },
  canceled: { key: 'billing.status.canceled', s: 'mute', glyph: '○' },
}

/**
 * The plan page's Billing section (Console D; the mock's "Billing · owners only"): subscribe to
 * Pro, or the subscription, its invoices and the portal, with the switch to reais. `#billing` is
 * where "Choose Pro" leads. With billing off (503 not_configured) it says how paying works, with
 * nothing to do; any other failure is an error.
 */
export function BillingSection({ orgId, role }: { orgId: string; role: Role }) {
  const { t } = useI18n()
  const billing = useQuery(billingQuery(orgId))
  if (billing.data === null)
    return (
      <div id="billing">
        <Section title={t('billing.title')} meta={t('billing.ownersMeta')} minor>
          <p className="se-p">{t('billing.off')}</p>
        </Section>
      </div>
    )
  return (
    <div id="billing">
      <Section title={t('billing.title')} meta={t('billing.ownersMeta')} minor>
        <div className="sections" style={{ gap: 14 }}>
          {billing.isPending ? (
            <Loading />
          ) : billing.isError ? (
            <ErrorState error={billing.error} />
          ) : billing.data.subscription && !ENDED.includes(billing.data.subscription.status) ? (
            <Subscription orgId={orgId} billing={billing.data} owner={role === 'owner'} />
          ) : role === 'owner' ? (
            <Subscribe orgId={orgId} billing={billing.data} />
          ) : (
            <p className="dim">{t('billing.ownersOnly')}</p>
          )}
        </div>
      </Section>
    </div>
  )
}

function Subscription({
  orgId,
  billing,
  owner,
}: {
  orgId: string
  billing: Billing
  owner: boolean
}) {
  const i18n = useI18n()
  const { t, number } = i18n
  const [opening, setOpening] = useState(false)
  const sub = billing.subscription as NonNullable<Billing['subscription']>
  const state = STATUS[sub.status] ?? (STATUS.incomplete as NonNullable<(typeof STATUS)[string]>)
  async function portal() {
    setOpening(true)
    try {
      await openBillingPortal(orgId)
    } finally {
      setOpening(false)
    }
  }
  const money = (cents: number, currency: string) =>
    number(cents / 100, { style: 'currency', currency: currency.toUpperCase() })
  return (
    <>
      {billing.switch ? <SwitchNotice orgId={orgId} billing={billing} owner={owner} /> : null}
      {sub.status === 'past_due' && !billing.switch ? (
        <div role="status" className="callout">
          <span className="g crash" aria-hidden="true">
            !
          </span>
          <p className="se-p">{t('billing.pastDue')}</p>
        </div>
      ) : sub.status === 'unpaid' ? (
        <div role="alert" className="callout">
          <span className="g crash" aria-hidden="true">
            !
          </span>
          <p className="se-p">{t('billing.unpaid')}</p>
        </div>
      ) : null}
      <dl className="kv">
        <dt>{t('billing.plan')}</dt>
        <dd>
          {t(PLAN_KEY[sub.plan])} ·{' '}
          {t(sub.base_interval === 'year' ? 'billing.yearly' : 'billing.monthly')}
        </dd>
        <dt>{t('billing.state')}</dt>
        <dd>
          <span className={`st ${state.s}`}>
            <span aria-hidden="true">{state.glyph} </span>
            <span>{t(state.key)}</span>
          </span>
        </dd>
        <dt>{sub.cancel_at_period_end ? t('billing.ends') : t('billing.nextInvoice')}</dt>
        <dd>{day(i18n, sub.period_end)}</dd>
        {billing.currency ? (
          <>
            <dt>{t('billing.currency')}</dt>
            <dd translate="no">{billing.currency.toUpperCase()}</dd>
          </>
        ) : null}
      </dl>
      {billing.invoices.length > 0 ? (
        <div className="scroll-x">
          <table className="t" aria-label={t('billing.invoices')}>
            <thead>
              <tr>
                <th scope="col">{t('billing.invoiceDate')}</th>
                <th scope="col" className="num">
                  {t('billing.invoiceAmount')}
                </th>
                <th scope="col" className="last">
                  {t('billing.invoice')}
                </th>
              </tr>
            </thead>
            <tbody>
              {billing.invoices.slice(0, 6).map((i) => (
                <tr key={i.stripe_created_at}>
                  <td>{day(i18n, i.stripe_created_at)}</td>
                  <td className="num">{money(i.amount_due, i.currency)}</td>
                  <td className="last" translate="no">
                    {i.hosted_invoice_url ? (
                      <a
                        className="ul"
                        href={i.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {i.number ?? t('billing.invoice')}
                      </a>
                    ) : (
                      (i.number ?? t('billing.invoice'))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {owner ? (
        <button
          type="button"
          className="btn"
          disabled={opening}
          onClick={portal}
          style={{ justifySelf: 'start' }}
        >
          {t('billing.manage')}
        </button>
      ) : (
        <Locked label={t('billing.manage')} by="owner" />
      )}
    </>
  )
}

/**
 * The switch to reais (D88 addendum): a card issued in Brazil on a USD subscription. Offered,
 * the owner adds the card again (Stripe keeps cards per currency) with the CPF or CNPJ and the
 * address; scheduled, it says when; past due, the open invoice can be paid with a card issued
 * outside Brazil, and support switches it now once the owner has prepared it.
 */
function SwitchNotice({
  orgId,
  billing,
  owner,
}: {
  orgId: string
  billing: Billing
  owner: boolean
}) {
  const i18n = useI18n()
  const { t } = i18n
  const [clientSecret, setClientSecret] = useState<string>()
  const [error, setError] = useState(false)
  const sw = billing.switch
  if (!sw) return null
  if (sw.state === 'scheduled')
    return (
      <div role="status" className="notice stack">
        <p>
          {t('billing.switch.scheduled', {
            date: sw.starts_at ? day(i18n, sw.starts_at) : '',
          })}
        </p>
        {sw.last_invoice_needs_foreign_card ? <p>{t('billing.switch.lastInvoiceCard')}</p> : null}
      </div>
    )
  if (sw.state === 'prepared')
    return (
      <p role="status" className="notice">
        {t('billing.switch.prepared')}
      </p>
    )
  async function start() {
    setError(false)
    try {
      setClientSecret((await billingSwitchSetupIntent(orgId)).client_secret)
    } catch {
      setError(true)
    }
  }
  return (
    <section className="notice stack" aria-label={t('billing.switch.title')}>
      <p style={{ fontWeight: 600 }}>{t('billing.switch.title')}</p>
      <p>{t(sw.past_due ? 'billing.switch.pastDue' : 'billing.switch.about')}</p>
      {sw.past_due && sw.open_invoice_url ? (
        <a href={sw.open_invoice_url} target="_blank" rel="noopener noreferrer">
          {t('billing.switch.payInvoice')}
        </a>
      ) : null}
      {error ? (
        <p role="alert" className="notice error">
          {t('state.failed')}
        </p>
      ) : null}
      {!owner ? (
        <p className="dim">{t('billing.ownersOnly')}</p>
      ) : clientSecret ? (
        <Elements stripe={stripeFor(billing.publishable_key)} options={{ clientSecret }}>
          <SwitchForm orgId={orgId} pastDue={sw.past_due} />
        </Elements>
      ) : (
        <button
          type="button"
          className="btn solid"
          onClick={start}
          style={{ justifySelf: 'start' }}
        >
          {t(sw.past_due ? 'billing.switch.prepare' : 'billing.switch.start')}
        </button>
      )}
    </section>
  )
}

/** The card issued in Brazil, on the customer in reais, with the CPF or CNPJ and the address. */
function SwitchForm({ orgId, pastDue }: { orgId: string; pastDue: boolean }) {
  const { t } = useI18n()
  const stripe = useStripe()
  const elements = useElements()
  const switchCurrency = useSwitchCurrency(orgId)
  const [intentId, setIntentId] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<MessageId>()
  const [taxId, setTaxId] = useState('')
  const [address, setAddress] = useState<BrazilAddress>({
    line1: '',
    city: '',
    state: '',
    postal_code: '',
  })
  const ids = {
    taxId: useId(),
    line1: useId(),
    city: useId(),
    state: useId(),
    postal: useId(),
  }

  async function confirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!stripe || !elements) return
    setBusy(true)
    setMessage(undefined)
    try {
      let id = intentId
      if (!id) {
        const confirmed = await stripe.confirmSetup({ elements, redirect: 'if_required' })
        if (confirmed.error || !confirmed.setupIntent) {
          setMessage('billing.cardFailed')
          return
        }
        id = confirmed.setupIntent.id
        setIntentId(id)
      }
      await switchCurrency.mutateAsync({ setup_intent_id: id, tax_id: taxId, address })
    } catch (error) {
      setMessage(isApiError(error, 400) ? 'billing.switch.invalid' : failureMessage(error, true))
    } finally {
      setBusy(false)
    }
  }

  const field = (key: keyof BrazilAddress, id: string, label: MessageId, auto: string) => (
    <div className="field">
      <label htmlFor={id}>{t(label)}</label>
      <input
        id={id}
        className="input"
        autoComplete={auto}
        required
        value={address[key] ?? ''}
        onChange={(e) => setAddress({ ...address, [key]: e.target.value })}
      />
    </div>
  )

  return (
    <form className="form" onSubmit={confirm} aria-label={t('billing.switch.form')}>
      {intentId ? null : <PaymentElement />}
      <p className="dim">{t('billing.brazilAbout')}</p>
      <div className="field">
        <label htmlFor={ids.taxId}>{t('billing.taxId')}</label>
        <input
          id={ids.taxId}
          className="input"
          inputMode="text"
          autoCapitalize="characters"
          maxLength={18}
          required
          value={taxId}
          onChange={(e) => setTaxId(e.target.value)}
        />
      </div>
      {field('line1', ids.line1, 'billing.address', 'address-line1')}
      {field('city', ids.city, 'billing.city', 'address-level2')}
      {field('state', ids.state, 'billing.stateField', 'address-level1')}
      {field('postal_code', ids.postal, 'billing.postalCode', 'postal-code')}
      {message ? (
        <p role="alert" className="notice error">
          {t(message)}
        </p>
      ) : null}
      <button
        type="submit"
        className="btn solid"
        disabled={busy || !stripe}
        style={{ justifySelf: 'start' }}
      >
        {t(pastDue ? 'billing.switch.prepareConfirm' : 'billing.switch.confirm')}
      </button>
    </form>
  )
}

function Subscribe({ orgId, billing }: { orgId: string; billing: Billing }) {
  const { t } = useI18n()
  const [interval, setInterval] = useState<'month' | 'year'>('month')
  const [clientSecret, setClientSecret] = useState<string>()
  const [error, setError] = useState(false)

  async function start() {
    setError(false)
    try {
      setClientSecret((await billingSetupIntent(orgId)).client_secret)
    } catch {
      setError(true)
    }
  }

  return (
    <>
      <p className="se-p">{t('billing.about')}</p>
      {/* Monthly | Yearly as the mock's segmented control, two radios underneath. */}
      <div className="se-sp">
        <fieldset className="seg" disabled={clientSecret !== undefined}>
          <legend className="vh">{t('billing.chooseInterval')}</legend>
          {(['month', 'year'] as const).map((i) => (
            <label key={i}>
              <input
                type="radio"
                name="interval"
                value={i}
                checked={interval === i}
                onChange={() => setInterval(i)}
              />
              {t(i === 'year' ? 'billing.yearly' : 'billing.monthly')}
            </label>
          ))}
        </fieldset>
        <p className="se-hint mono">{t('billing.yearlyHint')}</p>
      </div>
      {error ? (
        <p role="alert" className="notice error">
          {t('state.failed')}
        </p>
      ) : null}
      {clientSecret ? (
        <Elements stripe={stripeFor(billing.publishable_key)} options={{ clientSecret }}>
          <PaymentStep orgId={orgId} interval={interval} />
        </Elements>
      ) : (
        <button
          type="button"
          className="btn solid"
          onClick={start}
          style={{ justifySelf: 'start' }}
        >
          {t('billing.addPaymentMethod')}
        </button>
      )}
    </>
  )
}

function PaymentStep({ orgId, interval }: { orgId: string; interval: 'month' | 'year' }) {
  const { t } = useI18n()
  const stripe = useStripe()
  const elements = useElements()
  const subscribe = useSubscribe(orgId)
  const [intentId, setIntentId] = useState<string>()
  const [brazil, setBrazil] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<MessageId>()
  const [taxId, setTaxId] = useState('')
  const [address, setAddress] = useState<BrazilAddress>({
    line1: '',
    city: '',
    state: '',
    postal_code: '',
  })
  const ids = {
    taxId: useId(),
    line1: useId(),
    city: useId(),
    state: useId(),
    postal: useId(),
  }

  async function finish(
    setupIntentId: string,
    details?: { tax_id: string; address: BrazilAddress },
  ) {
    const result = await subscribe.mutateAsync({
      plan: 'pro',
      interval,
      setup_intent_id: setupIntentId,
      ...details,
    })
    // A first payment that needs the customer: 3-D Secure for a card, the voucher or QR code
    // for boleto and Pix. confirmPayment handles each method; the webhook then starts the plan.
    if (result.action_client_secret && stripe) {
      const confirmed = await stripe.confirmPayment({
        clientSecret: result.action_client_secret,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })
      if (confirmed.error) throw new PaymentError()
    }
  }

  async function confirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!stripe || !elements) return
    setBusy(true)
    setMessage(undefined)
    try {
      let id = intentId
      if (!id) {
        const confirmed = await stripe.confirmSetup({ elements, redirect: 'if_required' })
        if (confirmed.error || !confirmed.setupIntent) {
          setMessage('billing.cardFailed')
          return
        }
        id = confirmed.setupIntent.id
        setIntentId(id)
        const method = await billingPaymentMethod(orgId, id)
        if (method.brazil_details_required) {
          setBrazil(true)
          return
        }
        await finish(id)
      } else await finish(id, { tax_id: taxId, address })
    } catch (error) {
      setMessage(failureMessage(error, brazil))
    } finally {
      setBusy(false)
    }
  }

  const field = (key: keyof BrazilAddress, id: string, label: MessageId, auto: string) => (
    <div className="field">
      <label htmlFor={id}>{t(label)}</label>
      <input
        id={id}
        className="input"
        autoComplete={auto}
        required
        value={address[key] ?? ''}
        onChange={(e) => setAddress({ ...address, [key]: e.target.value })}
      />
    </div>
  )

  return (
    <form className="form" onSubmit={confirm} aria-label={t('billing.paymentForm')}>
      {brazil ? (
        <>
          <p className="dim">{t('billing.brazilAbout')}</p>
          <div className="field">
            <label htmlFor={ids.taxId}>{t('billing.taxId')}</label>
            <input
              id={ids.taxId}
              className="input"
              inputMode="text"
              autoCapitalize="characters"
              maxLength={18}
              required
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
            />
          </div>
          {field('line1', ids.line1, 'billing.address', 'address-line1')}
          {field('city', ids.city, 'billing.city', 'address-level2')}
          {field('state', ids.state, 'billing.stateField', 'address-level1')}
          {field('postal_code', ids.postal, 'billing.postalCode', 'postal-code')}
        </>
      ) : (
        <PaymentElement />
      )}
      {message ? (
        <p role="alert" className="notice error">
          {t(message)}
        </p>
      ) : null}
      <button
        type="submit"
        className="btn solid"
        disabled={busy || !stripe}
        style={{ justifySelf: 'start' }}
      >
        {t('billing.subscribe')}
      </button>
    </form>
  )
}
